"use client";

import { useEffect } from "react";

/**
 * First-party, cookie-free counts (1607): one beacon when a page is shown,
 * and one when a visitor follows an outbound link marked with
 * `data-eg-click` (website, booking, call, directions, menu, WhatsApp) or
 * saves a place. Fire-and-forget to /api/localized/beacon, which keeps a
 * daily count per subject and nothing about the visitor.
 */

export const BEACON_PATH = "/api/localized/beacon";

export type BeaconKind =
  | "view"
  | "pin_open"
  | "click_website"
  | "click_booking"
  | "click_call"
  | "click_directions"
  | "click_menu"
  | "click_whatsapp"
  | "save"
  | "share"
  | "search";

export function sendGuideBeacon(kind: BeaconKind, subjectType: "listing" | "event" | "post" | "page", subjectId: string) {
  try {
    const payload = JSON.stringify({ kind, subjectType, subjectId });
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(BEACON_PATH, new Blob([payload], { type: "application/json" }));
      return;
    }
    void fetch(BEACON_PATH, { method: "POST", headers: { "content-type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  } catch {
    // Counting must never break a page.
  }
}

const CLICK_KINDS: Record<string, BeaconKind> = {
  website: "click_website",
  booking: "click_booking",
  call: "click_call",
  directions: "click_directions",
  menu: "click_menu",
  whatsapp: "click_whatsapp",
  save: "save",
  share: "share",
};

/** Mount once per page: counts the view and listens for marked clicks. */
export function GuideBeacon({ subjectType, subjectId }: { subjectType: "listing" | "event" | "post" | "page"; subjectId: string }) {
  useEffect(() => {
    sendGuideBeacon("view", subjectType, subjectId);
  }, [subjectType, subjectId]);
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-eg-click]");
      if (!target) return;
      const kind = CLICK_KINDS[target.dataset.egClick ?? ""];
      if (!kind) return;
      sendGuideBeacon(kind, (target.dataset.egSubjectType as "listing" | "event" | "post" | "page" | undefined) ?? subjectType, target.dataset.egSubject ?? subjectId);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [subjectType, subjectId]);
  return null;
}
